import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLElement | undefined} quantityWarningBubble
 * @property {HTMLElement | undefined} quantityWarningBubbleText
 * @property {HTMLElement | undefined} quantityCartWarning
 * @property {HTMLElement | undefined} quantityCartWarningText
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const previousValue = quantityInput.value;

    quantityInput.stepUp();
    this.#onQuantityChange();

    if (
      previousValue === quantityInput.value &&
      quantityInput.max &&
      quantityInput.dataset.maxWarning &&
      parseInt(quantityInput.value, 10) === parseInt(quantityInput.max, 10)
    ) {
      this.#emitMaxWarning(true, parseInt(quantityInput.max, 10), quantityInput.dataset.maxWarning);
    }
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;
    const minValue = min ? parseInt(min, 10) : null;
    const maxValue = max ? parseInt(max, 10) : null;
    const numericValue = parseInt(newValue, 10);

    let maxClamped = false;

    if (minValue !== null && !Number.isNaN(minValue) && numericValue < minValue) {
      quantityInput.value = String(minValue);
    }

    if (maxValue !== null && !Number.isNaN(maxValue) && numericValue > maxValue) {
      quantityInput.value = String(maxValue);
      maxClamped = true;
    }

    const maxWarning = quantityInput.dataset.maxWarning;
    this.#emitMaxWarning(maxClamped, maxValue, maxWarning);
  };

  /**
   * Dispatches a quantity warning event.
   * @param {boolean} isActive
   * @param {number | null} maxValue
   * @param {string | null | undefined} warningMessage
   */
  #emitMaxWarning(isActive, maxValue, warningMessage) {
    const message = isActive && warningMessage ? warningMessage : null;

    this.#toggleWarningUI(message);

    const detail = {
      max: isActive ? maxValue : null,
      message,
    };

    this.dispatchEvent(
      new CustomEvent('quantity:max', {
        detail,
        bubbles: true,
      })
    );
  }

  /**
   * Updates the warning UI visibility/text for product and cart contexts.
   * @param {string | null} message
   */
  #toggleWarningUI(message) {
    const {
      quantityWarningBubble,
      quantityWarningBubbleText,
      quantityCartWarning,
      quantityCartWarningText,
    } = this.refs;

    if (quantityWarningBubble instanceof HTMLElement) {
      quantityWarningBubble.classList.toggle('hidden', !message);
    }

    if (quantityWarningBubbleText instanceof HTMLElement) {
      quantityWarningBubbleText.textContent = message ?? '';
    }

    if (quantityCartWarning instanceof HTMLElement) {
      quantityCartWarning.classList.toggle('hidden', !message);
    }

    if (quantityCartWarningText instanceof HTMLElement) {
      quantityCartWarningText.textContent = message ?? '';
    }
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
